import React, { useEffect, useState, useRef } from 'react';
import { View } from 'react-native';
import { BasicText, FlexBlock, FlexBlockWrapped, FlexElement, MetricStackTitle, PageWrapper, ShinyBlockOuter, ShinyBody, ShinyHeading, StrongText } from '../Styles';
import _ from 'lodash';
import BarChart from '../core/BarChart';
import { COLORS } from '../../WasteTheme';
import Metrics from '../core/Metrics';

import * as shape from 'd3-shape'
import { Path, Circle } from 'react-native-svg';
import moment from 'moment';
import styled from 'styled-components/native';
import Util from '../core/Util';

const SavingsLegendChildren = (data, hoveredBand) => {
    if (hoveredBand == null) {
        return null;
    }

    return <View style={{ flexDirection: "row", alignItems: "center" }}>
        <StrongText style={{ marginRight: 10 }}>{formatChartDate(hoveredBand.label, "MMMM YYYY")}</StrongText>
        <View style={{ height: 20, width: 20, backgroundColor: COLORS.wsc_blue, marginRight: 10 }} />
        <StrongText>${Util.roundNice(hoveredBand.value, 2) + " Costs"}</StrongText>
    </View>;
}

const SavingsBarChart = ({ portalData, using = "", title = "Savings" }) => {
    const [data, setData] = useState([]);
    const maxValue = useRef(0);

    useEffect(() => {
        const dates = {};
        _.forEach(portalData.dateRange || [], (date)=>{
            dates[moment(date).format("YYYY-MM")] = null;
        });

        
        let runningTotal = 0;
        let newData = _.map(portalData.savings, (row) => {
            dates[moment(row.date).format("YYYY-MM")] = "seen";

            runningTotal += row[using];

            return {
                label: row.date,
                value: runningTotal
            }
        });

        const unseenDates = _.filter(_.map(dates, (v,k)=>v==null?k:null), (el)=>el!=null);

        _.forEach(unseenDates, (date)=>{
            newData.push({
                label: date,
                value: 0
            });
        });

        newData = newData.sort((a,b)=>{
            return moment(a.label).diff(moment(b.label))
        });

        //fill in blanks
        let previousValue = 0;
        _.forEach(newData, (d)=>{
            if(d.value == 0 && previousValue != 0){
                d.value = previousValue;
            }

            previousValue = d.value;
        });

        maxValue.current = runningTotal;
        setData(newData);
    }, [portalData]);

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    const yAxisFormatLabel = (value, index) => {
        return Util.roundNice(value, 0);
    }

    const xAxisFormatLabel = (value, index) => {
        return formatChartDate(value);
    }

    const yearRange = [];
    let yearLabel = "";
    if(portalData.dateRange != undefined && portalData.dateRange.length > 0){
        yearRange.push(moment(portalData.dateRange[0]).format("YYYY"));

        const endRange = moment(portalData.dateRange[portalData.dateRange.length-1]).format("YYYY");
        if(endRange != yearRange[0]){
            yearRange.push(endRange);
        }

        yearLabel = yearRange.join(" - ");
    }

    return <ShinyBlockOuter minHeight={graphHeight}>
        <ShinyHeading>
            <MetricStackTitle>{title}</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart
                data={data}
                defaultDataPoint={data[data.length-1]}
                showLabels={false}
                svg={{ fill: COLORS.wsc_blue }}
                height={graphHeight}
                showGrid={true}
                showLegend={true}
                legendChildren={(data, hoveredBand) => SavingsLegendChildren(data, hoveredBand)}
                yMin={0}
                yMax={maxValue.current}
                xAccessor={({ item }) => { return item.label; }}
                xAxisFormatLabel={xAxisFormatLabel}
                yAccessor={({ item }) => { return item.value; }}
                yAxisFormatLabel={yAxisFormatLabel}
                yAxisLabel="CAD ($)"
                xAxisLabel={yearLabel}
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}

const legendChildren = (data, hoveredBand) => {
    if (hoveredBand == null) {
        return null;
    }

    const LegendFlexBlock = styled(FlexBlock)`
		justify-content: flex-start;
		align-items: flex-start;
		flex-direction: row;
	`;

    const ContentChunk = styled.View`
		flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        margin-right: 10px;
	`;

    return <LegendFlexBlock>
        <ContentChunk>
            <StrongText>{formatChartDate(hoveredBand.label, "MMMM YYYY")}</StrongText>
        </ContentChunk>

        <ContentChunk>
            <ContentChunk>
                <View style={{ height: 20, width: 20, backgroundColor: COLORS.navy, marginRight: 10 }} />
                <StrongText>${Util.roundNice(hoveredBand.value, 2) + " Costs"}</StrongText>
            </ContentChunk> 
            <ContentChunk>
                <View style={{ height: 3, width: 20, backgroundColor: COLORS.wsc_blue, marginRight: 10 }} />
                <StrongText>${Util.roundNice(hoveredBand.value2, 2) + " Costs Without WSC"}</StrongText>
            </ContentChunk>
        </ContentChunk>
    </LegendFlexBlock>;
}


const LineChartOnTop = (props) => {
    const { x, y, data, yAccessor, xAccessor, bandwidth } = props;

    const mappedData = data.map((item, index) => ({
        y: item.value2, //yAccessor({ item, index }),
        x: index //xAccessor({ item, index }),
    }))

    const line = shape
        .line()
        .x((d) => x(d.x) + bandwidth / 2)
        .y((d) => y(d.y))
        .defined((item) => typeof item.y === 'number')
        .curve(shape.curveLinear)(mappedData);

    const svg = { stroke: COLORS.wsc_blue, strokeWidth: 5 };

    return <><Path
        fill={'none'}
        {...svg}
        d={line}
        animate={false}
        animationDuration={0}
    />
        {mappedData.map((d, index) => <Circle
            key={index}
            cx={x(index) + bandwidth / 2}
            cy={y(d.y)}
            r={5}
            stroke={'white'}
            strokeWidth={2.5}
            fill={COLORS.wsc_blue}
        />)}
    </>
}

const CostBarChart = ({ portalData, title = "", using = "", includeLine = false, legend = false}) => {
    const [data, setData] = useState([]);
    const maxValue = useRef(0);

    useEffect(() => {
        const dates = {};
        _.forEach(portalData.dateRange || [], (date)=>{
            dates[moment(date).format("YYYY-MM")] = null;
        });

        let newData = _.map(portalData.savings, (row) => {
            dates[moment(row.date).format("YYYY-MM")] = "seen";

            return {
                label: row.date,
                value: row[using],
                value2: row["cost"] + row["avoidance"]
            }
        });

        const unseenDates = _.filter(_.map(dates, (v,k)=>v==null?k:null), (el)=>el!=null);

        _.forEach(unseenDates, (date)=>{
            newData.push({
                label: date,
                value: 0,
                value2: 0
            });
        });

        newData = newData.sort((a,b)=>{
            return moment(a.label).diff(moment(b.label))
        });

        setData(newData);
    }, [portalData]);

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    const yAxisFormatLabel = (value, index) => {
        let precision = 0;
        if(Math.abs(value) < 10){
            precision = 2;
        } else if(Math.abs(value) < 100){
            precision = 1;
        }

        if(index % 2 == 0){
            return Util.roundNice(value, precision);
        }

        return "";
    }

    const xAxisFormatLabel = (value, index) => {
        return formatChartDate(value);
    }

    const yearRange = [];
    let yearLabel = "";
    if(portalData.dateRange != undefined && portalData.dateRange.length > 0){
        yearRange.push(moment(portalData.dateRange[0]).format("YYYY"));

        const endRange = moment(portalData.dateRange[portalData.dateRange.length-1]).format("YYYY");
        if(endRange != yearRange[0]){
            yearRange.push(endRange);
        }

        yearLabel = yearRange.join(" - ");
    }

    return <ShinyBlockOuter minHeight={graphHeight} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>{title}</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart
                data={data}
                defaultDataPoint={data[data.length-1]}
                showLabels={false}
                svg={{ fill: COLORS.navy }}
                height={graphHeight}
                showGrid={true}
                showLegend={legend}
                legendChildren={legend ? (data, hoveredBand) => legendChildren(data, hoveredBand) : null}
                xAccessor={({ item }) => { return item.label; }}
                xAxisFormatLabel={xAxisFormatLabel}
                yAccessor={({ item }) => { return item.value; }}
                yAxisFormatLabel={yAxisFormatLabel}
                extraKids={includeLine ? <LineChartOnTop /> : null}
                yAxisLabel="CAD ($)"
                xAxisLabel={yearLabel}
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}

const FinanceDashboard = ({ portalData = {isSharedSavings: false, metrics: undefined}, endDate }) => {
    const [metricData, setMetricData] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(null);
    const categoricalData = useRef([]);

    useEffect(()=>{
        if(portalData.metrics != undefined){
            updateData();
        }
    }, [portalData]);

    const updateData = () => {
        let filterOutValue = "savings";

        if(portalData.isSharedSavings == true){
            filterOutValue = "avoidance";
        }

        categoricalData.current = _.orderBy(_.filter(portalData.metrics["finance"], (row)=>row.category != filterOutValue), "periodLength", "desc");

        setMetricData(_.groupBy(categoricalData.current, "category"));
    }

    return <PageWrapper>
        <BasicText>{currentMonth}</BasicText>
        <Metrics data={metricData} showDelta={false} />
        { portalData.isSharedSavings == true? null : <CostBarChart portalData={portalData} title="Total Cost Avoidance" using="cost" includeLine={true} legend={true}/> }
        { portalData.isSharedSavings == true? <SavingsBarChart portalData={portalData} using="avoidance" title="Cumulative Cost Avoidance" /> : null }
    </PageWrapper>
};

const formatChartDate = (d, customFormat) => {
    let format = "MMM";
    if(customFormat != undefined){
        format = customFormat;
    }

    if(Util.isMobile()){
        return moment(d).format(format);
    }
    return moment(d).format(format);
}

export default FinanceDashboard;